import { useForm as useHookForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { REGEX, CATEGORIES_ID } from 'src/constants/app';
import { allowNaNNumber } from 'src/utils/helper';

export type FormValue = {
  product: string;
  customer_name: string;
  destination: string;
  variety: string;
  shape: string;
  machiningMethod: string;
  product_machining: string;
  product_thickness: string;
  product_width: string;
  product_length: string;
  converted_product_thickness?: string;
  converted_product_width?: string;
  converted_product_length?: string;
  number_product: string;
  machining_thickness: string;
  machining_width: string;
  machining_length: string;
  machining_outside_diameter: string;
  machining_inside_diameter: string;
  options_size_unspecified: string;
  options_size_c: string;
  corner_option_a: string;
  corner_option_b: string;
  corner_option_c: string;
  corner_option_d: string;
  roll_eyes?: string;
  mill_sheets?: string;
  order_number: string;
  order_notes: string;
  // data optional
  destinationItem?: object;
  productItem?: Response.ProductItem;
  machiningItem?: {
    label: string;
    value: string;
  };
  sizeItem: object;
  chamferItem: object;
  seihnJoho?: string;
  industry?: string;
  region?: string;
  isResetByMachiningMethod?: boolean;
  showProcessingRange: string[];
};

const useForm = (labelError, isTrialMode, checkIsImportedProductOnlyAndZ0) => {
  const { t } = useTranslation();
  const schema = Yup.object().shape({
    title: Yup.string(),
    destination: isTrialMode
      ? Yup.string()
      : Yup.string().required(
          t('message.E000007', {
            0: t(`label.destination`),
          }),
        ),
    variety: Yup.string().required(
      t('message.E000003', {
        0: t('pricing_page.category_field.category_label'),
      }),
    ),

    shape: Yup.string().required(
      t('message.E000003', {
        0: t('pricing_page.category_field.shape_field'),
      }),
    ),

    product: Yup.string().required(
      t('message.E000003', {
        0: t('pricing_page.products_field'),
      }),
    ),
    machiningMethod: Yup.string()
      .nullable()
      .test({
        name: 'machiningMethod_required',
        test(value, context) {
          const variety = context.parent?.variety;
          // Do not validate if variety is TOOL_PRODUCTS (07)
          if (variety === CATEGORIES_ID.TOOL_PRODUCTS) {
            return true;
          }

          // Validate machiningMethod when variety exists and is not TOOL_PRODUCTS
          if (!value) {
            return context.createError({
              message: t('message.E000007', {
                0: t('pricing_page.machining_method_field'),
              }),
            });
          }

          return true;
        },
      }),
    product_thickness:
      labelError?.head &&
      Yup.string()
        .required(
          t('message.E000007', {
            0: labelError?.head,
          }),
        )
        .matches(
          REGEX.REGEX_PRODUCT_THICKNESS,
          t('message.E000012', {
            0: labelError?.head,
          }),
        ),
    seihnJoho: Yup.string()
      .nullable()
      .test({
        name: 'seihnJoho_required',
        test(value, context) {
          const variety = context.parent?.variety;
          // Only validate if variety is TOOL_PRODUCTS (07)
          if (variety === CATEGORIES_ID.TOOL_PRODUCTS) {
            if (!value) {
              return context.createError({
                message: t('message.E000007', {
                  0: t('pricing_page.type_selection'),
                }),
              });
            }
          }

          return true;
        },
      }),
    product_width: Yup.string()
      .nullable()
      .test({
        name: 'product_width_required',
        test(value, context) {
          const variety = context.parent?.variety;
          // Do not validate if variety is TOOL_PRODUCTS (07)
          if (variety === CATEGORIES_ID.TOOL_PRODUCTS) {
            return true;
          }

          // Validate product_width when conditions are met
          if (
            labelError?.size1Label &&
            !checkIsImportedProductOnlyAndZ0 &&
            !value
          ) {
            return context.createError({
              message: t('message.E000003', {
                0: labelError?.size1Label,
              }),
            });
          }

          return true;
        },
      }),
    product_length: Yup.string()
      .nullable()
      .test({
        name: 'product_length_required',
        test(value, context) {
          const variety = context.parent?.variety;
          // Do not validate if variety is TOOL_PRODUCTS (07)
          if (variety === CATEGORIES_ID.TOOL_PRODUCTS) {
            return true;
          }

          // Validate product_length when conditions are met
          if (
            labelError?.size2Label &&
            !checkIsImportedProductOnlyAndZ0 &&
            !value
          ) {
            return context.createError({
              message: t('message.E000003', {
                0: labelError?.size2Label,
              }),
            });
          }

          return true;
        },
      }),
    number_product: Yup.number()
      .required()
      .min(
        1,
        t('message.E000009', {
          0: t('pricing_page.product_size_field.number_field'),
          1: '1',
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    machining_thickness: Yup.string().nullable(),
    machining_width: Yup.string().nullable(),
    machining_length: Yup.string().nullable(),
    machining_outside_diameter: Yup.string().nullable(),
    machining_inside_diameter: Yup.string().nullable(),
    options_size_unspecified: Yup.string().nullable(),
    options_size_c: Yup.number()
      .max(
        99.9,
        t('message.E000053', {
          0: t('pricing_page.around_chamfer.error_C'),
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    corner_option_a: Yup.number()
      .max(
        99.9,
        t('message.E000053', {
          0: t('pricing_page.corner_handling_options.error_A'),
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    corner_option_b: Yup.number()
      .max(
        99.9,
        t('message.E000053', {
          0: t('pricing_page.corner_handling_options.error_B'),
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    corner_option_c: Yup.number()
      .max(
        99.9,
        t('message.E000053', {
          0: t('pricing_page.corner_handling_options.error_C'),
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    corner_option_d: Yup.number()
      .max(
        99.9,
        t('message.E000053', {
          0: t('pricing_page.corner_handling_options.error_D'),
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
  });

  const formInfo = useHookForm<FormValue>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  return { formInfo };
};

export default useForm;
